import { Stack, Heading, Paragraph, DataTable } from '@customerio/pluma-components/react';
import { useMemo } from 'react';
import { createColumnHelper } from '@tanstack/table-core';
import { useListWorkspaces } from '@/features/workspaces/hooks';
import type { MergedWorkspace } from '@/features/workspaces';
import { useSampleWorkspace } from '@/utils/sample-workspace';

interface SelectWorkspaceProps {
  selectedWorkspaces: Record<string, boolean>;
  onSelectWorkspaces: (workspaces: Record<string, boolean>) => void;
}

export function SelectWorkspace({ selectedWorkspaces, onSelectWorkspaces }: SelectWorkspaceProps) {
  const workspaces = useListWorkspaces();
  const { isSampleWorkspace } = useSampleWorkspace();

  const columnHelper = createColumnHelper<MergedWorkspace>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => <Heading level="6">{info.getValue()}</Heading>,
      }),
      columnHelper.accessor('id', {
        header: 'ID',
        meta: { align: 'center', width: 120 },
        cell: (info) => (
          <Paragraph variant="secondary" size="s">
            {info.getValue()}
          </Paragraph>
        ),
      }),
    ],
    [columnHelper],
  );

  const activeWorkspaces = useMemo(() => {
    if (workspaces.data == null) return [];
    return workspaces.data.filter((workspace) => workspace.cio_initialized);
  }, [workspaces.data]);

  const filteredWorkspaces = useMemo(() => {
    return activeWorkspaces.filter((workspace) => {
      return !isSampleWorkspace(workspace.id);
    });
  }, [activeWorkspaces, isSampleWorkspace]);

  return (
    <Stack gap="100">
      <Paragraph variant="secondary" size="s">
        Allow this app to access your workspaces
      </Paragraph>

      <DataTable
        data={filteredWorkspaces}
        columns={columns}
        caption="Available workspaces for OAuth access"
        withRowSelection={true}
        rowSelection={selectedWorkspaces}
        onRowSelectionChange={onSelectWorkspaces}
        getRowId={(row) => row.id.toString()}
        shouldHighlightOnHover={true}
        withSearch={true}
        isLoading={workspaces.isLoading}
        search={{ shouldSearchOnInput: true, shouldSearchOnClear: true }}
        emptyState={{
          title: 'No matching results found',
          icon: 'workspaces',
          description: 'Try adjusting your search.',
        }}
        withPagination={true}
        pagination={{
          pageSize: 10,
          pageSizes: [10, 20, 50],
        }}
      />
    </Stack>
  );
}
